import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const A4_W = 210;
const A4_H = 297;
const MARGIN = 10;
const FOOTER_H = 10;
const CONTENT_W = A4_W - 2 * MARGIN;
const CONTENT_H = A4_H - 2 * MARGIN - FOOTER_H;

const DEFAULT_FOOTER_LEFT = 'Simulatierapport';

interface SectionCapture {
  canvas: HTMLCanvasElement;
  heightMm: number;
}

async function captureSection(el: HTMLElement): Promise<SectionCapture> {
  // Use actual element width for accurate capture (no forced 794px override)
  const actualWidth = el.scrollWidth || el.offsetWidth;
  const captureWidth = Math.max(actualWidth, 794);

  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#FFFFFF',
    logging: false,
    width: captureWidth,
    windowWidth: captureWidth,
    onclone: (_doc, clonedEl) => {
      clonedEl.style.width = `${captureWidth}px`;
      clonedEl.style.maxWidth = `${captureWidth}px`;
      clonedEl.style.overflow = 'visible';
    },
  });

  const pxPerMm = canvas.width / CONTENT_W;
  const heightMm = canvas.height / pxPerMm;

  return { canvas, heightMm };
}

function addFooter(pdf: jsPDF, pageNum: number, totalPages: number, footerLeft: string) {
  const y = A4_H - MARGIN - 2;
  pdf.setDrawColor(200, 206, 214);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, y - 3, A4_W - MARGIN, y - 3);
  pdf.setFontSize(7);
  pdf.setTextColor(136, 153, 170);
  pdf.text(footerLeft, MARGIN, y);
  const rightText = `Pagina ${pageNum} van ${totalPages}`;
  pdf.text(rightText, A4_W - MARGIN, y, { align: 'right' });
}

/**
 * Generic PDF generator.
 * Collects all [data-pdf-section] elements + .print-section elements
 * in DOM order within the container, deduplicates, and renders them
 * across A4 pages with automatic pagination.
 * 
 * Sections taller than one page are scaled down proportionally to fit
 * a single page (no cropping). Each section starts on the current page
 * if there is room, otherwise on a new page.
 */
export async function generatePDF(
  elementId: string,
  filename: string,
  incognito?: boolean,
  footerOverride?: string
): Promise<void> {
  const footerLeft = footerOverride || DEFAULT_FOOTER_LEFT;
  const container = document.getElementById(elementId);
  if (!container) throw new Error(`Element #${elementId} not found`);

  // Collect ALL pdf sections and print-sections in DOM order, deduplicated
  const allSections = container.querySelectorAll('[data-pdf-section], .print-section');
  const seen = new Set<HTMLElement>();
  const sectionEls: HTMLElement[] = [];

  allSections.forEach(el => {
    const htmlEl = el as HTMLElement;
    if (!seen.has(htmlEl)) {
      seen.add(htmlEl);
      sectionEls.push(htmlEl);
    }
  });

  if (sectionEls.length === 0) throw new Error('No PDF sections found');

  // Capture all sections
  const captures: SectionCapture[] = [];
  for (const el of sectionEls) {
    captures.push(await captureSection(el));
  }

  // Layout: place sections on pages
  const pdf = new jsPDF('p', 'mm', 'a4');
  let currentY = MARGIN;
  let pageCount = 1;
  const GAP = 3; // mm gap between sections

  for (let i = 0; i < captures.length; i++) {
    const cap = captures[i];
    let renderW = CONTENT_W;
    let renderH = cap.heightMm;

    // Scale down if section taller than full page content area
    if (renderH > CONTENT_H) {
      const scaleFactor = CONTENT_H / renderH;
      renderH = CONTENT_H;
      renderW = CONTENT_W * scaleFactor;
    }

    // Check if it fits on current page
    const spaceLeft = CONTENT_H - (currentY - MARGIN);
    if (renderH > spaceLeft && currentY > MARGIN) {
      pdf.addPage();
      pageCount++;
      currentY = MARGIN;
    }

    // Center horizontally if scaled
    const xOffset = MARGIN + (CONTENT_W - renderW) / 2;

    const imgData = cap.canvas.toDataURL('image/jpeg', 0.92);
    pdf.addImage(imgData, 'JPEG', xOffset, currentY, renderW, renderH);
    currentY += renderH + GAP;
  }

  // Add footers to all pages
  const totalPages = pageCount;
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    addFooter(pdf, p, totalPages, footerLeft);
  }

  pdf.save(filename);
}
